import { createSlice, isFulfilled, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { getEntityId, showApiErrors } from '@/utils';
import { initialState, USERS_LIST_SLICE_NAME, UsersListState } from './models';
import {
  activateUser,
  deactivateUser,
  deleteUser,
  getUserDetails,
  getUsersList,
  updateUserDetails,
} from './actionCreators';
import { TUserFiltersValues } from '../helpers/types';
import { UserStatus } from '@/models/users.model';

export const slice = createSlice({
  name: USERS_LIST_SLICE_NAME,
  initialState,
  reducers: {
    resetUsersListState() {
      return { ...initialState };
    },
    updateUserFilters: (state: UsersListState, action: PayloadAction<TUserFiltersValues>) => {
      state.userFilters = { ...(state.userFilters ?? {}), ...action.payload } as TUserFiltersValues;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserDetails.fulfilled, (state: UsersListState, action) => {
        state.userDetails = action.payload;
        state.isLoading = false;
      })
      .addCase(getUsersList.fulfilled, (state: UsersListState, action) => {
        const { payload, meta } = action;
        state.usersList = { ...action.payload, pagination: { ...payload.cursor, limit: meta.arg.limit } };
        state.isLoading = false;
      })
      .addCase(deleteUser.fulfilled, (state: UsersListState) => {
        state.isLoading = false;
      })
      .addCase(activateUser.fulfilled, (state: UsersListState, action) => {
        const updatedList = (state.usersList?.items ?? []).map((user) =>
          getEntityId(user.PK) === action.meta.arg ? { ...user, status: UserStatus.ACTIVE } : user,
        );
        state.usersList = { ...state.usersList, items: updatedList };

        // Update current user if it is the one being edited
        if (state.userDetails?.PK && getEntityId(state?.userDetails?.PK) === action.meta.arg) {
          state.userDetails = { ...state.userDetails, status: UserStatus.ACTIVE };
        }

        state.isLoading = false;
      })
      .addCase(deactivateUser.fulfilled, (state: UsersListState, action) => {
        const updatedList = (state.usersList?.items ?? []).map((user) => {
          return getEntityId(user.PK) === action.meta.arg ? { ...user, status: UserStatus.DEACTIVATED } : user;
        });
        state.usersList = { items: updatedList };

        // Update current user if it is the one being edited
        if (state.userDetails?.PK && getEntityId(state?.userDetails?.PK) === action.meta.arg) {
          state.userDetails = { ...state.userDetails, status: UserStatus.DEACTIVATED };
        }

        state.isLoading = false;
      })
      .addMatcher(isPending(updateUserDetails, deleteUser, getUsersList), (state: UsersListState) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(isFulfilled(updateUserDetails), (state: UsersListState, action) => {
        state.userDetails = action.payload;
        state.isLoading = false;
      })
      .addMatcher(isRejected(updateUserDetails, deleteUser), (state: UsersListState, action) => {
        const { error } = action;
        state.isLoading = false;
        state.error = error;

        showApiErrors(error);
      });
  },
});

export const { resetUsersListState, updateUserFilters } = slice.actions;

export default slice.reducer;
